import { ref } from 'vue';
import { ExtractedFieldsArraySchema, type ExtractedField, type ParsedField, type FieldLocation } from '@/types/field';

export function useFieldData() {
  const fields = ref<ParsedField[]>([]);
  const selectedField = ref<ParsedField | null>(null);
  const fieldSelectionTrigger = ref(0);
  const error = ref<string>('');
  const rawOutput = ref<string>('');

  /**
   * Flatten nested extracted fields into a flat array for display
   * Includes path and depth information for nested fields
   */
  const flattenExtractedFields = (
    extractedFields: ExtractedField[],
    parentPath: string = '',
    depth: number = 0
  ): ParsedField[] => {
    const result: ParsedField[] = [];

    for (const field of extractedFields) {
      const fieldPath = parentPath ? `${parentPath}.${field.name}` : field.name;

      // Check if location is valid (has required properties)
      const hasValidLocation =
        field.location &&
        typeof field.location === 'object' &&
        'page' in field.location &&
        'bbox' in field.location &&
        'fabricjs' in field.location;

      // Determine if the field is editable (primitive types only)
      const isEditable = ['string', 'number', 'boolean'].includes(field.type);

      const parsedField: ParsedField = {
        name: field.name,
        value: field.value,
        location: hasValidLocation ? (field.location as FieldLocation) : null,
        type: field.type,
        description: field.description,
        required: field.required,
        path: fieldPath,
        depth: depth,
        isEditable,
      };

      result.push(parsedField);

      // Recursively flatten nested properties
      if (field.properties && field.properties.length > 0) {
        const nestedFields = flattenExtractedFields(field.properties, fieldPath, depth + 1);
        result.push(...nestedFields);
      }
    }

    return result;
  };

  /**
   * Extract JSON array from a string that may contain extra text before/after
   */
  const extractJsonArray = (str: string): string | null => {
    // Find the first '[' and last ']' to extract the JSON array
    const firstBracket = str.indexOf('[');
    const lastBracket = str.lastIndexOf(']');

    if (firstBracket === -1 || lastBracket === -1 || lastBracket < firstBracket) {
      return null;
    }

    return str.slice(firstBracket, lastBracket + 1);
  };

  const parseJsonString = (jsonString: string): void => {
    // Always store the raw output for display if parsing fails
    rawOutput.value = jsonString;

    try {
      // First try direct parsing
      let parsed: unknown;
      try {
        parsed = JSON.parse(jsonString);
      } catch {
        // If direct parsing fails, try to extract JSON array from the string
        // (AI might have added extra text before/after the JSON)
        const extracted = extractJsonArray(jsonString);
        if (extracted) {
          console.warn('Extracting JSON array from response with extra content');
          parsed = JSON.parse(extracted);
        } else {
          throw new Error('Could not find JSON array in response');
        }
      }

      // Check if it's an array (new format)
      if (Array.isArray(parsed)) {
        // Validate against the new array-based schema
        const result = ExtractedFieldsArraySchema.safeParse(parsed);

        if (!result.success) {
          console.error('Schema validation error:', result.error.format());
          // Try to use the data anyway if it's a valid array with name/type fields
          if (parsed.length > 0 && parsed[0].name && parsed[0].type) {
            console.warn('Schema validation failed but data looks usable, proceeding anyway');
            fields.value = flattenExtractedFields(parsed as ExtractedField[]);
            error.value = '';
            return;
          }
          error.value = 'Malformed JSON output. The data cannot be parsed as structured fields.';
          fields.value = [];
          return;
        }

        const extractedFields: ExtractedField[] = result.data;
        fields.value = flattenExtractedFields(extractedFields);
        error.value = '';
      } else {
        // Not an array - show as malformed
        error.value = 'Malformed JSON output. Expected an array of fields.';
        fields.value = [];
        console.error('Expected array but got:', typeof parsed);
      }
    } catch (e) {
      error.value = 'Malformed JSON output. The data cannot be parsed as structured fields.';
      fields.value = [];
      console.error('JSON parsing error:', e);
    }
  };

  const selectField = (field: ParsedField) => {
    selectedField.value = field;
    fieldSelectionTrigger.value++; // Increment to trigger re-scroll even for same field
  };

  const clearSelection = () => {
    selectedField.value = null;
  };

  const clearFields = () => {
    fields.value = [];
    selectedField.value = null;
    error.value = '';
    rawOutput.value = '';
  };

  return {
    fields,
    selectedField,
    fieldSelectionTrigger,
    error,
    rawOutput,
    parseJsonString,
    selectField,
    clearSelection,
    clearFields,
  };
}
